import { useState, useEffect } from 'react';
import { Globe, ExternalLink, AlertTriangle, MapPin, Radio, Activity, Layers, Eye } from 'lucide-react';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import { usePage } from '../contexts/PageContext.jsx';

// Sprint 21.16: GEO INTEL HUB (ADR-057)
// "Consume don't rebuild" — embed/link external geopolitical tools +
// surface ingested signals (ACLED + RSS) on a single page.

const TIER_COLORS = { core: '#10b981', active: '#3b82f6', monitor: '#6b7280' };
const SEVERITY_COLORS = {
  critical: '#ef4444',
  high: '#f59e0b',
  medium: '#eab308',
  low: '#6b7280',
};
const REGION_COLORS = {
  gulf: '#fb923c',
  mena: '#f97316',
  east_asia: '#a855f7',
  europe: '#3b82f6',
  africa: '#84cc16',
  latam: '#ec4899',
  global: '#6b7280',
};
const CATEGORY_ICONS = {
  live_tracker: Radio,
  analysis: Layers,
  data_feed: Activity,
  news: Eye,
  risk_index: AlertTriangle,
};

function ToolTile({ tool }) {
  const tierColor = TIER_COLORS[tool.tier] || '#6b7280';
  const Icon = CATEGORY_ICONS[tool.category] || Globe;
  return (
    <a
      href={tool.url}
      target="_blank"
      rel="noopener noreferrer"
      className="block px-3 py-2.5 bg-korinth-bg rounded border border-korinth-border/30 hover:border-korinth-accent/50 transition-colors group"
    >
      <div className="flex items-start gap-2">
        <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: tierColor }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-mono font-bold text-white truncate">{tool.name}</span>
            <ExternalLink className="w-3 h-3 text-korinth-muted opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
          </div>
          {tool.description && (
            <p className="text-[10px] font-mono text-korinth-muted leading-relaxed mt-0.5 line-clamp-2">{tool.description}</p>
          )}
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            <span className="text-[8px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded font-bold"
                  style={{ color: tierColor, backgroundColor: tierColor + '15' }}>
              {tool.tier}
            </span>
            <span className="text-[8px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded text-korinth-muted bg-korinth-bg border border-korinth-border/30">
              {String(tool.category).replace('_', ' ')}
            </span>
            {(tool.region_focus || []).slice(0, 3).map(r => (
              <span key={r} className="text-[8px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded"
                    style={{ color: REGION_COLORS[r] || '#6b7280', backgroundColor: (REGION_COLORS[r] || '#6b7280') + '15' }}>
                {r.replace('_', ' ')}
              </span>
            ))}
          </div>
        </div>
      </div>
    </a>
  );
}

function ToolGrid({ tools, tier }) {
  const filtered = (tools || []).filter(t => t.tier === tier);
  if (filtered.length === 0) {
    return <p className="text-[10px] font-mono text-korinth-muted italic">No {tier} tools curated.</p>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
      {filtered.map(t => <ToolTile key={t.slug} tool={t} />)}
    </div>
  );
}

function SignalRow({ signal }) {
  const sevColor = SEVERITY_COLORS[signal.severity] || '#6b7280';
  const regColor = REGION_COLORS[signal.region] || '#6b7280';
  const ageMs = signal.detected_at ? Date.now() - new Date(signal.detected_at + (signal.detected_at?.endsWith('Z') ? '' : 'Z')).getTime() : null;
  const ageStr = ageMs == null ? 'n/a'
    : ageMs < 60_000 ? '<1m'
    : ageMs < 3_600_000 ? `${Math.round(ageMs / 60_000)}m`
    : ageMs < 86_400_000 ? `${Math.round(ageMs / 3_600_000)}h`
    : `${Math.round(ageMs / 86_400_000)}d`;
  return (
    <div className="px-3 py-2 bg-korinth-bg rounded border border-korinth-border/30">
      <div className="flex items-start gap-2 flex-wrap">
        <span className="text-[8px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded font-bold"
              style={{ color: sevColor, backgroundColor: sevColor + '20' }}>
          {signal.severity}
        </span>
        <span className="text-[8px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded"
              style={{ color: regColor, backgroundColor: regColor + '15' }}>
          {signal.region?.replace('_', ' ') || 'global'}
        </span>
        <span className="text-[9px] font-mono text-korinth-muted">{signal.source}</span>
        <span className="text-[9px] font-mono text-korinth-muted">·</span>
        <span className="text-[9px] font-mono text-korinth-muted">{String(signal.signal_type).replace(/_/g, ' ')}</span>
        <span className="text-[9px] font-mono text-korinth-muted ml-auto">{ageStr} ago</span>
      </div>
      <p className="text-[11px] font-mono text-white leading-relaxed mt-1">
        {signal.source_url ? (
          <a href={signal.source_url} target="_blank" rel="noopener noreferrer" className="hover:text-korinth-accent">
            {signal.raw_signal} <ExternalLink className="inline w-3 h-3" />
          </a>
        ) : (
          signal.raw_signal
        )}
      </p>
      {((signal.related_tickers && signal.related_tickers.length > 0) || (signal.related_sectors && signal.related_sectors.length > 0)) && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {(signal.related_tickers || []).map(t => (
            <span key={t} className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-korinth-bg border border-korinth-accent/30 text-korinth-accent">{t}</span>
          ))}
          {(signal.related_sectors || []).map(s => (
            <span key={s} className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-korinth-bg border border-korinth-border/30 text-korinth-muted">{s}</span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function GeoIntelPage() {
  const { setPage } = usePage();
  useEffect(() => { setPage('geo-intel'); }, []);

  const [tools, setTools] = useState([]);
  const [toolCounts, setToolCounts] = useState(null);
  const [signals, setSignals] = useState([]);
  const [signalSummary, setSignalSummary] = useState(null);
  const [freshness, setFreshness] = useState(null);
  const [regionFilter, setRegionFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');

  useEffect(() => {
    async function load() {
      const json = async (url) => { try { const r = await fetch(url); return r.ok ? await r.json() : null; } catch { return null; } };
      const toolsWrap = await json('/api/geo-intel/tools');
      setTools(toolsWrap?.tools || []);
      setToolCounts(toolsWrap?.counts || null);

      const params = new URLSearchParams();
      if (regionFilter) params.set('region', regionFilter);
      if (severityFilter) params.set('severity', severityFilter);
      params.set('hours', '168');
      params.set('limit', '50');
      const sigWrap = await json(`/api/geo-intel/signals?${params}`);
      setSignals(sigWrap?.signals || []);
      setSignalSummary(sigWrap?.summary || null);

      const fresh = await json('/api/geo-intel/signals/freshness');
      setFreshness(fresh || null);
    }
    load();
    const iv = setInterval(load, 120_000);
    return () => clearInterval(iv);
  }, [regionFilter, severityFilter]);

  const freshColor = freshness?.freshness === 'fresh' ? '#10b981'
    : freshness?.freshness === 'stale' ? '#f59e0b'
    : '#ef4444';

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Globe className="w-6 h-6 text-korinth-accent" />
        <h1 className="text-2xl font-bold text-white">Geo Intel Hub</h1>
        <span className="text-korinth-muted text-sm">External tools + ingested signals (ADR-057)</span>
      </div>

      {/* Top stats strip */}
      <div className="bg-korinth-bg/50 border border-korinth-border/30 rounded-lg px-4 py-2.5 text-[11px] font-mono text-korinth-text flex flex-wrap items-center gap-4">
        <span><span className="text-korinth-muted">Tools curated:</span> <span className="text-white font-bold">{(toolCounts?.core || 0) + (toolCounts?.active || 0) + (toolCounts?.monitor || 0)}</span></span>
        <span style={{ color: TIER_COLORS.core }}>{toolCounts?.core || 0}c</span>
        <span style={{ color: TIER_COLORS.active }}>{toolCounts?.active || 0}a</span>
        <span style={{ color: TIER_COLORS.monitor }}>{toolCounts?.monitor || 0}m</span>
        <span><span className="text-korinth-muted">Signals 24h:</span> <span className="text-white font-bold">{signalSummary?.last_24h || 0}</span></span>
        <span><span className="text-korinth-muted">Signals 7d:</span> <span className="text-white font-bold">{signalSummary?.last_7d || 0}</span></span>
        <span><span className="text-korinth-muted">High/Crit 7d:</span> <span className="text-korinth-warning font-bold">{signalSummary?.high_critical_7d || 0}</span></span>
        {freshness && (
          <span className="ml-auto" style={{ color: freshColor }}>
            data: {freshness.freshness} {freshness.age_minutes != null ? `(${freshness.age_minutes}min ago)` : ''}
          </span>
        )}
      </div>

      {/* Section 1: External tools (CORE) */}
      <ModuleContainer title="🌐 Core Geopolitical Tools" subtitle="Always-on coverage — click to open in new tab">
        <ToolGrid tools={tools} tier="core" />
      </ModuleContainer>

      {/* Section 2: Recent signals timeline */}
      <ModuleContainer
        title="📡 Recent Geopolitical Signals"
        subtitle={`${signals.length} signals${freshness?.acled_configured === false ? ' (ACLED key not configured — RSS-only ingestion)' : ''}`}
      >
        <div className="space-y-3">
          {/* Filter strip */}
          <div className="flex flex-wrap items-center gap-2 text-[10px] font-mono">
            <span className="text-korinth-muted">filter:</span>
            <select
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value)}
              className="bg-korinth-bg border border-korinth-border/30 rounded px-2 py-1 text-white"
            >
              <option value="">all regions</option>
              <option value="gulf">gulf</option>
              <option value="mena">mena</option>
              <option value="east_asia">east_asia</option>
              <option value="europe">europe</option>
              <option value="africa">africa</option>
              <option value="latam">latam</option>
              <option value="global">global</option>
            </select>
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className="bg-korinth-bg border border-korinth-border/30 rounded px-2 py-1 text-white"
            >
              <option value="">all severities</option>
              <option value="critical">critical</option>
              <option value="high,critical">high+critical</option>
              <option value="medium,high,critical">medium+</option>
            </select>
            {freshness?.by_source && freshness.by_source.length > 0 && (
              <span className="ml-auto text-korinth-muted">
                sources: {freshness.by_source.map(s => `${s.source}:${s.n}`).join(' · ')}
              </span>
            )}
          </div>

          {/* Signals list */}
          {signals.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <MapPin className="w-8 h-8 mx-auto text-korinth-muted mb-2" />
              <p className="text-[11px] text-korinth-muted font-mono">No signals match the current filter.</p>
              <p className="text-[10px] text-korinth-muted font-mono mt-1">RSS cron fires every hour at :50 UTC. ACLED at :40 UTC every 6h (requires API key registration).</p>
            </div>
          ) : (
            <div className="space-y-1.5 max-h-[640px] overflow-y-auto">
              {signals.map(s => <SignalRow key={s.id} signal={s} />)}
            </div>
          )}
        </div>
      </ModuleContainer>

      {/* Section 3: Active background tools */}
      <ModuleContainer title="📊 Active Background Tools" subtitle="Click-to-open data feeds + risk indices">
        <ToolGrid tools={tools} tier="active" />
      </ModuleContainer>

      {/* Section 4: Monitor tools */}
      <ModuleContainer title="📚 Monitor (Reference / On-Demand)" subtitle="Lower-frequency consultation">
        <ToolGrid tools={tools} tier="monitor" />
      </ModuleContainer>

      {/* Footer note */}
      <p className="text-[9px] font-mono text-korinth-muted text-center italic">
        Tier curation per ADR-049 — explicit user choice, never auto-derived. Mutate via PUT /api/geo-intel/tools/:slug.
        Geopolitical specialist (Phase 2 #5/12) ships in Sprint 21.17 once geopolitical_signals accumulates 7-14 days of data.
      </p>
    </div>
  );
}
